import React from 'react';
import { connect } from 'react-redux';
import { ButtonGroup, Button, Menu, MenuItem } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CreatePageConstants from '../../CreatePageConstants';
import { createActions } from '../../store';
import { appOperations } from '../../../../../App/store';
import { randomString } from '../../../../../utilities/randomUtilities';
import AddIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { convertToLyricsTime, hasSegmentCollision } from '../../../../../utilities/lyricsUtilities';
import { createObjectCopy } from '../../../../../utilities/objectUtilities';
import ConfirmationDialog from '../../../../../components/dialogs/ConfirmationDialog/ConfirmationDialog';

import srtParser2 from "srt-parser-2";

const styles = theme => ({
    timelineActionsGroup: {
        position: 'absolute',
        left: 52,
        display: 'none',
        width: 284,
        ".MuiButtonGroup-firstButton": {
            borderColor: `${theme.palette.primary.main} !important`
        }
    },
    addSegmentButton: {
        height: '100%',
        width: '80%',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        background: theme.palette.background.paper,
        '&:hover': {
            background: theme.palette.background.paper
        }
    },
    moreOptionsButton: {
        height: '100%',
        width: '20%',
        background: theme.palette.background.paper,
        '&:hover': {
            background: theme.palette.background.paper
        }
    },
    uploadSrtLabel: {
        cursor: "pointer"
    },
});

class TimelineActionsButton extends React.Component {
    state = {
        timelineActionsMenuAnchor: null,
        overwriteSegmentsConfirmationOpen: false,
        srtSegments: []
    };

    render() {
        const { classes, lyricsTimelineContextMenuOpen, addLyricsSegments, lyricsActionsPanelTime, lyricsSegments } = this.props;
        const { timelineActionsMenuAnchor } = this.state;
        const checkedSegmentsPresent = Boolean((lyricsSegments.filter(s => s.checked)).length);
        
        return (
            <>
                <ButtonGroup 
                    id='timeline-actions-panel'
                    variant='outlined'
                    color='primary'
                    aria-label='Timeline actions'
                    size='small'
                    className={classes.timelineActionsGroup}
                    style={{
                        display: (lyricsActionsPanelTime !== -1) ? 'block' : 'none',
                        top: lyricsActionsPanelTime * CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR,
                        height: CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION * CreatePageConstants.LYRICS_TIMELINE.SCALE_FACTOR
                    }}
                >
                    <Button
                        className={classes.addSegmentButton}
                        variant='outlined'
                        color='primary' 
                        onClick={this.onNewSegmentAddClick}
                        aria-label='add new segment'
                        startIcon={<AddIcon/>}
                    >
                        Add Lyrics
                    </Button>
                    <Button
                        className={classes.moreOptionsButton}
                        variant='outlined' 
                        onClick={this.onActionsMenuOpen}
                    >
                        <KeyboardArrowDownRoundedIcon/>
                    </Button>
                </ButtonGroup>
                <Menu
                    anchorEl={timelineActionsMenuAnchor}
                    keepMounted
                    open={lyricsTimelineContextMenuOpen && Boolean(timelineActionsMenuAnchor)}
                    onClose={this.onActionsMenuClose}
                >
                    <MenuItem onClick={this.onPasteLyricsClick}>
                        Paste Lyrics
                    </MenuItem>
                    <MenuItem 
                        onClick={this.onPasteSelectionClick} 
                        disabled={!checkedSegmentsPresent}
                    >
                        Paste Selection
                    </MenuItem>
                    <MenuItem>
                        <label className={classes.uploadSrtLabel} htmlFor="file">Upload SRT</label>
                        <input onClick={this.onConfirmSrtOveride} accept=".srt" type="file" id="file" hidden onChange={(e)=> this.onSrtFileUpload(e)} />
                    </MenuItem>
                </Menu>
                <ConfirmationDialog
                    title={'Overwrite Lyrics?'}
                    open={this.state.overwriteSegmentsConfirmationOpen}
                    onClose={() => this.setState({ overwriteSegmentsConfirmationOpen: false })}
                    confirmation={() => addLyricsSegments(this.state.srtSegments)}
                    storageKey='OVERWRITE_LYRICS_CONFIRMATION'
                >
                    Uploading an SRT file will cause your existing lyrics to be deleted.
                </ConfirmationDialog>
            </>
        );
    }

    onSrtFileUpload(event) {
        const { setProperty, audioDuration, lyricsSegments, showInformationDialog, addLyricsSegments } = this.props;

        setProperty('lyricsTimelineContextMenuOpen', false);

        if (event.target.files[0]) {
            var parser = new srtParser2();
            const file = event.target.files[0];
            const fileExtension = file.name.split('.').pop();

            if (!CreatePageConstants.SUPPORTED_LYRICS_FILE_EXTENSIONS.includes(fileExtension)) {
                showInformationDialog(`Lyric parsing failed`, `Make sure you've uploaded a valid SRT file.`);
                return;
            }   

            const fileReader = new FileReader();
            fileReader.readAsText(event.target.files[0]);

            fileReader.onload = () => {
                let convertedSegments = [];
                const srtSegments = parser.fromSrt(fileReader.result);

                if (!srtSegments.length)
                    return;

                srtSegments.forEach(segment => {
                    if (segment.endSeconds > audioDuration) {
                        showInformationDialog("Unable to add all lyrics", `The lyrics file is too long. Some lyrics were not added to the video.`);
                        return;
                    }

                    convertedSegments.push({
                        id: randomString(),
                        text: segment.text,
                        startTime: segment.startSeconds,
                        endTime: segment.endSeconds
                    });
                });

                const sectionStartTime = convertedSegments[0].startTime;
                const sectionEndTime = convertedSegments[convertedSegments.length - 1].endTime;

                if (lyricsSegments.some(s => s.startTime >= sectionStartTime && s.endTime <= sectionEndTime)) {
                    this.setState({ overwriteSegmentsConfirmationOpen: true, srtSegments: convertedSegments });
                } else {
                    addLyricsSegments(convertedSegments);
                }
            };
        }
        event.target.value = null;
    }

    onActionsMenuOpen = (event) => {
        const { setProperty } = this.props;
        this.setState({ timelineActionsMenuAnchor: event.currentTarget });
        setProperty('lyricsTimelineContextMenuOpen', true);
    }

    onActionsMenuClose = () => {
        const { setProperty } = this.props;
        this.setState({ timelineActionsMenuAnchor: null });
        setProperty('lyricsTimelineContextMenuOpen', false);
    }

    onNewSegmentAddClick = () => {
        const { addLyricsSegment, lyricsActionsPanelTime, hideLyricsActionsButton } = this.props;
        
        // Assign the time range for a segment
        const startTime = lyricsActionsPanelTime;
        const endTime = convertToLyricsTime(startTime + CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_DURATION);

        // Hide actions button
        hideLyricsActionsButton();

        // Create the new segment object
        let newSegment = {
            id: randomString(),
            text: CreatePageConstants.LYRICS_TIMELINE.DEFAULT_SEGMENT_TEXT,
            startTime: startTime,
            endTime: endTime
        };

        // Update the segments in store 
        addLyricsSegment(newSegment);
    }

    onPasteLyricsClick = () => {
        const { setProperty, lyricsActionsPanelTime } = this.props;

        this.setState({ timelineActionsMenuAnchor: null });
        setProperty('lyricsTextDialogInitialTime', lyricsActionsPanelTime);
        setProperty('lyricsTimelineContextMenuOpen', false);
        setProperty('lyricsTextDialogOpen', true);
    }

    onPasteSelectionClick = () => {
        const { setProperty, addLyricsSegment, lyricsActionsPanelTime, lyricsSegments, audioDuration, hideLyricsActionsButton } = this.props;

        this.setState({ timelineActionsMenuAnchor: null });
        setProperty('lyricsTimelineContextMenuOpen', false);
        hideLyricsActionsButton();

        const checkedSegments = lyricsSegments.filter(s => s.checked);
        const zeroPointTime = checkedSegments[0].startTime;

        for (let i = 0; i < checkedSegments.length; i++) {
            const checkedSegment = checkedSegments[i];
            const newSegment = createObjectCopy(checkedSegment);

            newSegment.startTime = convertToLyricsTime(lyricsActionsPanelTime + (checkedSegment.startTime - zeroPointTime));
            newSegment.endTime = convertToLyricsTime(newSegment.startTime + (checkedSegment.endTime - checkedSegment.startTime));

            if (!hasSegmentCollision(newSegment.startTime, newSegment.endTime, lyricsSegments, audioDuration)) {
                newSegment.id = randomString();
                newSegment.checked = false;
                addLyricsSegment(newSegment);
            } else {
                break;
            }
        }
    }
}

const mapStateToProps = (state) => {
    const { 
        lyricsActionsPanelTime,
        lyricsTimelineContextMenuOpen,
        audioDuration
    } = state.create;
    
    const { segments: lyricsSegments } = state.createPreview.present.videoSettings.lyrics;

    return {
        lyricsActionsPanelTime,
        lyricsTimelineContextMenuOpen,
        lyricsSegments,
        audioDuration
    };
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const addLyricsSegment = (newSegment) => dispatch(createActions.addLyricsSegment(newSegment));
    const addLyricsSegments = (newSegments) => dispatch(createActions.addLyricsSegments(newSegments));
    const setPageProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const showInformationDialog = (title, message) => dispatch(appOperations.showInformationDialog(true, title, message));
    const hideLyricsActionsButton = () => dispatch(createActions.setProperty('lyricsActionsPanelTime', -1));
    
    return {
        setProperty,
        addLyricsSegment,
        addLyricsSegments,
        hideLyricsActionsButton,
        setPageProperty,
        showInformationDialog
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(TimelineActionsButton, styles));